import React, { useCallback, useEffect, useRef, useState } from "react";
import { Auth } from "aws-amplify";
import { withRouter } from "react-router-dom";
import RoutesAuth from "./RoutesAuth";
import { Provider } from "react-redux";
import configureStore from "./store";
import { resetStore } from "./actions";

const store = configureStore();
const TEPCO_ACCESS_KEY = "td94cq1n92ivx6q7hpu3uxvn";

export const resolveAccessKey = ({
  isDeaAdmin = false,
  switchedAccessKey = "",
  sessionAccessKey = "",
  cognitoStorageAccessKey = "",
  storedAccessKey = "",
  fallbackAccessKey = TEPCO_ACCESS_KEY,
} = {}) => {
  if (isDeaAdmin && switchedAccessKey) {
    return switchedAccessKey;
  }
  if (sessionAccessKey) {
    return sessionAccessKey;
  }
  if (cognitoStorageAccessKey) {
    return cognitoStorageAccessKey;
  }
  if (storedAccessKey) {
    return storedAccessKey;
  }
  return fallbackAccessKey;
};

const getAccessKeyFromCognitoUserData = () => {
  try {
    const userDataKey = Object.keys(localStorage).find(
      (key) =>
        key.includes("CognitoIdentityServiceProvider") &&
        key.endsWith(".userData")
    );
    if (!userDataKey) {
      return "";
    }
    const raw = localStorage.getItem(userDataKey);
    if (!raw) {
      return "";
    }
    const parsed = JSON.parse(raw);
    const attrs = Array.isArray(parsed?.UserAttributes) ? parsed.UserAttributes : [];
    const accessKeyAttr = attrs.find((attr) => attr.Name === "custom:access_key");
    const value = accessKeyAttr?.Value;
    return value && value !== "undefined" && value !== "null" ? value : "";
  } catch (e) {
    return "";
  }
};

const App = (props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isDeaAdmin, setIsDeaAdmin] = useState(false);
  const [accessKey, setAccessKey] = useState(() => localStorage.getItem("access_key"));
  const authLifecycle = useRef(0);

  const setInteractiveAdminStatus = useCallback((admin) => {
    authLifecycle.current += 1;
    setIsDeaAdmin(admin === true);
  }, []);

  const userHasAuthenticated = useCallback((authenticated) => {
    setIsAuthenticated(authenticated);
  }, []);

  const handleLogout = useCallback(async () => {
    authLifecycle.current += 1;
    try {
      await Auth.signOut();
    } finally {
      window.localStorage.clear();
      setAccessKey(null);
      setIsDeaAdmin(false);
      store.dispatch(resetStore());
      userHasAuthenticated(false);
      props.history.push("/signin");
    }
  }, [props.history, userHasAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    const refreshCognitoSession = (cognitoUser, refreshToken) =>
      new Promise((resolve, reject) => {
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(session);
        });
      });

    const refreshSession = async () => {
      const lifecycleAtStart = authLifecycle.current;
      try {
        setIsAuthenticated(true);
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();
        const session = await refreshCognitoSession(cognitoUser, currentSession.refreshToken);
        if (!isMounted || lifecycleAtStart !== authLifecycle.current) {
          return;
        }

        userHasAuthenticated(true);
        localStorage.setItem("idToken", session.idToken.jwtToken);

        const idTokenPayload = session?.idToken?.payload || {};
        const accessTokenPayload = session?.accessToken?.payload || {};
        const userSession = cognitoUser?.signInUserSession || {};
        const userIdTokenPayload = userSession?.idToken?.payload || {};
        const userAccessTokenPayload = userSession?.accessToken?.payload || {};
        const groupsFromIdToken = idTokenPayload["cognito:groups"];
        const groupsFromAccessToken = accessTokenPayload["cognito:groups"];
        const groupsFromUserIdToken = userIdTokenPayload["cognito:groups"];
        const groupsFromUserAccessToken = userAccessTokenPayload["cognito:groups"];
        const groups = [
          ...(Array.isArray(groupsFromIdToken)
            ? groupsFromIdToken
            : groupsFromIdToken
              ? [groupsFromIdToken]
              : []),
          ...(Array.isArray(groupsFromAccessToken)
            ? groupsFromAccessToken
            : groupsFromAccessToken
              ? [groupsFromAccessToken]
              : []),
          ...(Array.isArray(groupsFromUserIdToken)
            ? groupsFromUserIdToken
            : groupsFromUserIdToken
              ? [groupsFromUserIdToken]
              : []),
          ...(Array.isArray(groupsFromUserAccessToken)
            ? groupsFromUserAccessToken
            : groupsFromUserAccessToken
              ? [groupsFromUserAccessToken]
              : []),
        ];
        const isDeaAdmin = groups.includes("dea_admin");
        setIsDeaAdmin(isDeaAdmin);

        if (isDeaAdmin) {
          localStorage.setItem("dea_role", "admin");
        } else {
          localStorage.removeItem("dea_role");
          localStorage.removeItem("admin_switched_access_key");
        }

        const sessionAccessKey =
          idTokenPayload["custom:access_key"] ||
          accessTokenPayload["custom:access_key"] ||
          userIdTokenPayload["custom:access_key"] ||
          userAccessTokenPayload["custom:access_key"] ||
          cognitoUser?.attributes?.["custom:access_key"];
        const normalizedSessionAccessKey =
          sessionAccessKey && sessionAccessKey !== "undefined" && sessionAccessKey !== "null"
            ? sessionAccessKey
            : "";
        const currentStoredAccessKey = localStorage.getItem("access_key");
        const normalizedStoredAccessKey =
          currentStoredAccessKey &&
          currentStoredAccessKey !== "undefined" &&
          currentStoredAccessKey !== "null"
            ? currentStoredAccessKey
            : "";
        const accessKeyFromCognitoStorage = getAccessKeyFromCognitoUserData();
        const effectiveAccessKey = resolveAccessKey({
          isDeaAdmin,
          switchedAccessKey: localStorage.getItem("admin_switched_access_key"),
          sessionAccessKey: normalizedSessionAccessKey,
          cognitoStorageAccessKey: accessKeyFromCognitoStorage,
          storedAccessKey: normalizedStoredAccessKey,
        });
        localStorage.setItem("access_key", effectiveAccessKey);
        setAccessKey(effectiveAccessKey);
      } catch (e) {
        if (isMounted && lifecycleAtStart === authLifecycle.current) {
          userHasAuthenticated(false);
          setIsDeaAdmin(false);
          setAccessKey(null);
          localStorage.removeItem("dea_role");
          localStorage.removeItem("admin_switched_access_key");
          console.log("Unable to refresh Token:", e);
        }
      } finally {
        if (isMounted) {
          setIsAuthenticating(false);
        }
      }
    };

    refreshSession();

    return () => {
      isMounted = false;
    };
  }, [userHasAuthenticated]);

  const childProps = {
    isAuthenticated,
    isAuthenticating,
    isDeaAdmin,
    setIsDeaAdmin: setInteractiveAdminStatus,
    userHasAuthenticated,
    handleLogout,
    accessKey,
  };

  if (isAuthenticating) {
    return null;
  }

  return (
    <Provider store={store}>
      <div className="App">
        <RoutesAuth childProps={childProps} />
      </div>
    </Provider>
  );
};

export default withRouter(App);
